import React, {
  AnchorHTMLAttributes,
  forwardRef,
  MutableRefObject,
} from "react";
import { cx } from "class-variance-authority";
import Link from "next/link";
import { FiArrowLeft, FiLoader } from "react-icons/fi";

export type VariantEnum =
  | "primary-magenta"
  | "primary-navy"
  | "secondary"
  | "tertiary"
  | "quarternary"
  | "ghost"
  | "ghost-destructive"
  | "destructive"
  | "eskimo"
  | "base"
  | "outline";
export interface ButtonProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, "ref"> {
  variant: VariantEnum;
  loading?: boolean;
}

const COLORS: Record<VariantEnum, string> = {
  base: "",

  destructive: "bg-destructive text-white",
  "primary-magenta": "bg-magenta text-white focus:ring-magenta",
  "primary-navy": "bg-navy text-white focus:ring-navy",
  secondary:
    "bg-secondary text-gray-8 disabled:opacity-100 disabled:text-gray-6 focus:ring-secondary",
  tertiary:
    "textnavy bg-tertiary disabled:opacity-100 disabled:bg-[#E6ECF5] disabled:text-[#839BAF]",
  quarternary:
    "bg-secondary text-[#CF251D] disabled:opacity-100 disabled:text-gray-6",
  ghost: "bg-ghost text-current hover:bg-[#F6F6F6]",
  eskimo: "bg-friendly-eskimo text-navy",
  "ghost-destructive":
    "bg-ghost text-current hover:text-destructive hover:bg-[#F6F6F6]",
  outline: "border border-[#dce3eb]",
};

const Group: React.FC<{
  variant?: "tight" | "default";
  className?: string;
}> = ({ children, variant = "default", className }) => {
  // const spacing = variant === "default" ? "space-x-1" : "space-x-[8px]";
  return <div className={cx("flex items-center")}>{children}</div>;
};

export const getButtonClasses = (
  variant: VariantEnum,
  className?: string,
  loading?: boolean,
) => {
  const bg = COLORS[variant];

  return cx(
    loading ? "cursor-wait" : "disabled:cursor-not-allowed cursor-pointer",
    "inline-flex items-center select-none justify-center hover:opacity-90 disabled:opacity-50 m-0 p-0 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-[8px]",
    bg,
    className,
    "ds-debug",
  );
};

export const Button = React.forwardRef(
  (
    {
      className,
      children,
      type,
      onClick,
      loading,
      variant,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    return (
      <button
        {...props}
        disabled={props.disabled ?? loading}
        onClick={onClick}
        className={getButtonClasses(variant, className, loading)}
        ref={ref as MutableRefObject<HTMLButtonElement>}
      >
        {loading ? <FiLoader className="animate-spin" /> : children}
      </button>
    );
  },
);

export const Large = React.forwardRef(
  ({ className, children, ...props }: ButtonProps, ref) => {
    return (
      <Button
        {...props}
        className={cx("px-[28px] py-[18px] text-lg font-semibold", className)}
        ref={ref as MutableRefObject<HTMLButtonElement>}
      >
        {children}
      </Button>
    );
  },
);

export const Standard = React.forwardRef(
  ({ className, children, ...props }: ButtonProps, ref) => {
    return (
      <Button
        {...props}
        className={cx(
          "px-[16px] py-[10px] text-base font-semibold",
          className,
          props.loading ? "h-10" : "",
        )}
        ref={ref as MutableRefObject<HTMLButtonElement>}
      >
        {children}
      </Button>
    );
  },
);

export const Micro = React.forwardRef(
  ({ className, children, ...props }: ButtonProps, ref) => {
    return (
      <Button
        {...props}
        className={cx(
          "flex h-[32px] items-center px-[12px] text-sm font-semibold",
          className,
        )}
        ref={ref as MutableRefObject<HTMLButtonElement>}
      >
        {children}
      </Button>
    );
  },
);

export const IconButtonStandard = React.forwardRef(
  ({ className, children, ...props }: ButtonProps, ref) => {
    return (
      <Button
        {...props}
        className={cx("px-[12px] py-[12px] text-lg font-semibold", className)}
        ref={ref as MutableRefObject<HTMLButtonElement>}
      >
        {children}
      </Button>
    );
  },
);

export const IconButtonMicro = React.forwardRef(
  ({ className, children, ...props }: ButtonProps, ref) => {
    return (
      <Button
        {...props}
        className={cx("px-[8px] py-[8px] text-lg font-semibold", className)}
        ref={ref as MutableRefObject<HTMLButtonElement>}
      >
        {children}
      </Button>
    );
  },
);

export const Base = React.forwardRef(
  ({ className, children, ...props }: ButtonProps, ref) => {
    return (
      <Button
        {...props}
        ref={ref as MutableRefObject<HTMLButtonElement>}
        className={cx(className)}
      >
        {children}
      </Button>
    );
  },
);

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  variant?: VariantEnum;
  onConfirm?: () => void;
  removePrefix?: boolean;
}

export const LinkUnstyled = ({
  variant,
  className,
  children,
  href,
  onConfirm,
  removePrefix,
  ...props
}: LinkProps) => {
  return removePrefix ? (
    <a {...props} href={href} className={className}>
      {children}
    </a>
  ) : (
    <Link href={href}>
      <a {...props} className={className}>
        {children}
      </a>
    </Link>
  );
};
export const LinkBase = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { variant, className, children, href, onConfirm, removePrefix, ...props },
    ref,
  ) => {
    const bg = COLORS[variant];
    const classes = cx(
      "inline-flex items-center select-none justify-center hover:opacity-90 disabled:opacity-50 m-0 p-0 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 rounded-[8px]",
      bg,
      className,
      "ds-debug",
    );

    return removePrefix ? (
      <a {...props} href={href} className={classes}>
        {children}
      </a>
    ) : (
      <Link href={href}>
        <a {...props} className={classes} ref={ref}>
          {children}
        </a>
      </Link>
    );
  },
);

export const LinkMicro = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, ...props }, ref) => {
    const classes = cx(
      "text-sm px-[8px] py-[8px] h-8 font-semibold",
      className,
    );

    return <LinkBase {...props} className={classes} ref={ref} />;
  },
);

export const LinkStandard = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, ...props }, ref) => {
    const classes = cx(
      "text-base px-[16px] py-[10px] font-semibold",
      className,
    );

    return <LinkBase {...props} className={classes} ref={ref} />;
  },
);

export const LinkLarge = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, ...props }, ref) => {
    const classes = cx("text-lg px-[28px] py-[18px] font-semibold", className);

    return <LinkBase {...props} className={classes} ref={ref} />;
  },
);

export const LinkBack = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, ...props }, ref) => {
    const classes = cx(
      "gap-x-2 text-base font-medium py-2 px-2 -ml-1 text-light-elephant",
      className,
    );

    return (
      <LinkBase {...props} className={classes} ref={ref}>
        <FiArrowLeft size={16} />
        <span>{props.children}</span>
      </LinkBase>
    );
  },
);

const Buttons = {
  Base,
  Micro,
  Standard,
  Large,
  IconButtonMicro,
  IconButtonStandard,
  Group,
  LinkBase,
  LinkMicro,
  LinkStandard,
  LinkLarge,
  LinkBack,
  LinkUnstyled,
};

export default Buttons;
